"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { parseAppUtcDate } from "@/lib/format-datetime"
import {
  fetchAgentPortfolio,
  fetchLiveMetrics,
  fetchDashboardFeed,
  fetchPortfolioHistory,
  fetchTradeHistory,
  type AgentFleetResponse,
  type BackendPortfolio,
  type PortfolioHistoryPoint,
  type RunCycleResponse,
  type SystemAnalysisResponse,
  type TradeHistoryStats,
  type TransparencyResponse,
  type UniverseQuote,
  type FxSessionContext,
  type MaeAnalyticsPayload,
} from "@/lib/agent-force"

/** Full dashboard poll: DB-backed dashboard feed + live MT5 portfolio. */
export const DASHBOARD_AUTO_REFRESH_MS = 5 * 60 * 1000
/** Lightweight live portfolio poll for top value cards only. */
export const PORTFOLIO_LIVE_REFRESH_MS = 40 * 1000

const TWENTY_FOUR_H_MS = 24 * 60 * 60 * 1000

function parseTimeMs(iso: unknown): number | null {
  if (iso == null || iso === "") return null
  const d = parseAppUtcDate(String(iso))
  return d ? d.getTime() : null
}

/** Scheduled / history rows in the “last 24h” window; undated rows stay visible. */
function isWithinLast24h(iso: unknown): boolean {
  const ms = parseTimeMs(iso)
  if (ms === null) return true
  return Date.now() - ms <= TWENTY_FOUR_H_MS
}

const AGENT_MT5_MAGIC = new Set([11001, 11002, 11003])

/**
 * Open MT5 positions (always listed), broker MT5 deal legs when provided, plus SQLite trade_history.
 * When `mt5Deals` is non-empty, closed `trade_history` rows whose `mt5_ticket` appears as a deal
 * `position_id` are omitted to avoid duplicating full closes that MT5 already lists leg-by-leg.
 */
function buildRecentTradesAndPositions(
  portfolio: BackendPortfolio | null,
  historyItems: Array<Record<string, unknown>>,
  mt5Deals: Array<Record<string, unknown>> = [],
): Array<Record<string, unknown>> {
  const rows: Array<Record<string, unknown> & { _sort_at: number }> = []
  const openTickets = new Set<string>()
  const openSymbols = new Set<string>()

  const dealPositionIds = new Set<number>()
  for (const d of mt5Deals) {
    if (d == null || typeof d !== "object") continue
    const pid = Number((d as { position_id?: unknown }).position_id)
    if (Number.isFinite(pid) && pid > 0) dealPositionIds.add(pid)
  }
  const hasMt5DealLedger = dealPositionIds.size > 0

  const rawPos = portfolio?.open_positions ?? portfolio?.positions ?? []
  if (Array.isArray(rawPos)) {
    for (const p of rawPos) {
      if (p == null || typeof p !== "object") continue
      const pos = p as Record<string, unknown>
      if (pos.ticket != null) openTickets.add(String(pos.ticket))
      if (pos.symbol != null) openSymbols.add(String(pos.symbol).toUpperCase())
      const mag = Number(pos.magic)
      rows.push({
        ...pos,
        row_type: "open_position",
        is_agent_deal: Number.isFinite(mag) && AGENT_MT5_MAGIC.has(mag),
        _sort_at: parseTimeMs(pos.opened_at_utc ?? pos.opened_at) ?? Date.now(),
      })
    }
  }

  for (const t of historyItems) {
    if (t == null || typeof t !== "object") continue
    const status = String(t.status ?? "").toLowerCase()
    // Trade-history keeps execution events. When a live position exists for the same ticket/symbol,
    // keep the "Book" open-position row only to avoid duplicate rows in the Recent Trades card.
    const ticket = t.mt5_ticket != null ? String(t.mt5_ticket) : ""
    const ticketNum = t.mt5_ticket != null ? Number(t.mt5_ticket) : NaN
    const symbol = String(t.symbol ?? "").toUpperCase()
    const isOpenLike = status === "open" || status === "pending_close"
    const hasMatchingLivePosition = (ticket && openTickets.has(ticket)) || (!!symbol && openSymbols.has(symbol))
    if (isOpenLike && !hasMatchingLivePosition) {
      // Never present "open" history rows unless MT5 still reports the position as live.
      continue
    }
    if (isOpenLike && hasMatchingLivePosition) continue

    if (status === "closed" && hasMt5DealLedger && Number.isFinite(ticketNum)) {
      if (dealPositionIds.has(ticketNum)) continue
      // Also hide when any deal leg shares this order ticket (legacy rows stored order id not position id).
      const orderMatch = mt5Deals.some((d) => {
        const order = Number((d as { order_ticket?: unknown }).order_ticket)
        return Number.isFinite(order) && order === ticketNum
      })
      if (orderMatch) continue
    }

    const when =
      t.closed_at_local ??
      t.opened_at_local ??
      t.updated_at_local ??
      t.created_at_local ??
      t.closed_at ??
      t.opened_at ??
      t.updated_at ??
      t.created_at
    // Keep all closed history for analytics context; keep fresh pending/open events.
    if (status !== "closed" && !isWithinLast24h(when)) continue
    rows.push({
      ...t,
      row_type: "trade_history",
      is_agent_deal: true,
      _sort_at: parseTimeMs(when) ?? 0,
    })
  }

  for (const d of mt5Deals) {
    if (d == null || typeof d !== "object") continue
    const time = (d as { time?: unknown }).time
    rows.push({
      ...d,
      row_type: "mt5_deal",
      _sort_at: parseTimeMs(time) ?? 0,
    })
  }

  rows.sort((a, b) => b._sort_at - a._sort_at)
  return rows.map(({ _sort_at, ...rest }) => rest)
}

export interface MarketRow {
  pair: string
  bid: string
  ask: string
  change: string
  trend: "up" | "down" | "flat"
}

export interface AgentDashboardData {
  portfolio: BackendPortfolio | null
  portfolioLiveUpdatedAt: string | null
  portfolioLiveError: string | null
  marketRows: MarketRow[]
  pairAnalysis: Record<string, RunCycleResponse>
  agentFeed: Array<{
    id: string
    agent: string
    stage: string
    symbol: string
    timestamp: string
    cycleRecordedAt: string | null | undefined
    status: "active" | "pending" | "completed"
    content: string
    reasoning: string[]
    rawResponse: unknown
  }>
  recentTrades: Array<Record<string, unknown>>
  tradeStats: TradeHistoryStats | null
  transparency: TransparencyResponse | null
  agentFleet: AgentFleetResponse | null
  systemAnalysis: SystemAnalysisResponse | null
  portfolioHistory: PortfolioHistoryPoint[]
  sessionContext: FxSessionContext | null
  maeAnalytics: MaeAnalyticsPayload | null
  loading: boolean
  error: string | null
  refresh: (options?: { silent?: boolean }) => Promise<void>
}

function toPair(symbol: string): string {
  const s = String(symbol || "").replace(/[^A-Za-z]/g, "").toUpperCase()
  if (s.length < 6) return String(symbol || "")
  const logical = s.slice(0, 6)
  return `${logical.slice(0, 3)}/${logical.slice(3)}`
}

function formatPct(raw: number): string {
  const signed = raw >= 0 ? `+${raw.toFixed(2)}` : raw.toFixed(2)
  return `${signed}%`
}

function formatPriceForSymbol(symbol: string, value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0.0000"
  const s = symbol.toUpperCase()
  if (s.includes("JPY") || s.includes("XAU")) {
    return value.toFixed(2)
  }
  return value.toFixed(4)
}

export function useAgentDashboardData(): AgentDashboardData {
  const [portfolio, setPortfolio] = useState<BackendPortfolio | null>(null)
  const [portfolioLiveUpdatedAt, setPortfolioLiveUpdatedAt] = useState<string | null>(null)
  const [portfolioLiveError, setPortfolioLiveError] = useState<string | null>(null)
  const [pairAnalysis, setPairAnalysis] = useState<Record<string, RunCycleResponse>>({})
  const [tradeHistoryItems, setTradeHistoryItems] = useState<Array<Record<string, unknown>>>([])
  const [mt5DealsLedger, setMt5DealsLedger] = useState<Array<Record<string, unknown>>>([])
  const [transparency, setTransparency] = useState<TransparencyResponse | null>(null)
  const [agentFleet, setAgentFleet] = useState<AgentFleetResponse | null>(null)
  const [systemAnalysis, setSystemAnalysis] = useState<SystemAnalysisResponse | null>(null)
  const [portfolioHistory, setPortfolioHistory] = useState<PortfolioHistoryPoint[]>([])
  const [quotes, setQuotes] = useState<UniverseQuote[]>([])
  const [tradeStats, setTradeStats] = useState<TradeHistoryStats | null>(null)
  const [sessionContext, setSessionContext] = useState<FxSessionContext | null>(null)
  const [maeAnalytics, setMaeAnalytics] = useState<MaeAnalyticsPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true
    try {
      if (!silent) {
        setError(null)
        setLoading(true)
      }

      // Fire live metrics + portfolio history in parallel so the chart can render
      // before the heavier dashboard-feed completes.
      const portfolioPromise = fetchLiveMetrics()
        .then((x) => x.portfolio)
        .catch(() => fetchAgentPortfolio())
      const earlyHistoryPromise = fetchPortfolioHistory(730).catch(() => null)

      const portfolioRes = await portfolioPromise
      setPortfolio(portfolioRes)
      setPortfolioLiveUpdatedAt(new Date().toISOString())
      setPortfolioLiveError(null)

      // Apply early history as soon as it arrives so the chart renders without
      // waiting for dashboard-feed (which also includes MT5 + fleet JSON work).
      const earlyHistory = await earlyHistoryPromise
      if (earlyHistory?.points && earlyHistory.points.length > 0) {
        setPortfolioHistory(earlyHistory.points)
      }

      // Dashboard feed bundles DB data; if it fails, still show MT5 open positions and try trade-history alone.
      let historyItems: Array<Record<string, unknown>> = []
      let nextTradeStats: TradeHistoryStats | null = null
      let feedWarning: string | null = null

      try {
        const feed = await fetchDashboardFeed()
        const transparencyRes = feed.transparency
        const historyRes = feed.trade_history
        // Overwrite with feed version (may be fresher than the early fetch)
        setPortfolioHistory(feed.portfolio_history?.points ?? [])
        setTransparency(transparencyRes)
        setAgentFleet(feed.agent_fleet ?? null)
        setSystemAnalysis(feed.system_analysis ?? null)
        setQuotes((feed.quotes?.items || []).filter((item) => item.status === "ok"))
        setSessionContext(feed.session_context ?? null)
        setMaeAnalytics(feed.mae_analytics ?? null)
        const cycles = (transparencyRes.records || [])
          .filter((record) => record.run_cycle_output)
          .map((record) => ({
            symbol: record.requested_symbol,
            data: record.run_cycle_output as RunCycleResponse,
          }))
        setPairAnalysis(
          Object.fromEntries(cycles.map((it) => [toPair(it.symbol), it.data])),
        )
        nextTradeStats = historyRes.stats ?? null
        historyItems = (historyRes.items as unknown as Array<Record<string, unknown>>) ?? []
        setMt5DealsLedger((historyRes.mt5_deals as unknown as Array<Record<string, unknown>>) ?? [])
      } catch (feedErr) {
        feedWarning = feedErr instanceof Error ? feedErr.message : "Dashboard feed failed"
        try {
          const th = await fetchTradeHistory(400)
          nextTradeStats = th.stats ?? null
          historyItems = (th.items as unknown as Array<Record<string, unknown>>) ?? []
          setMt5DealsLedger((th.mt5_deals as unknown as Array<Record<string, unknown>>) ?? [])
        } catch {
          historyItems = []
          setMt5DealsLedger([])
        }
      }

      setTradeStats(nextTradeStats)
      setTradeHistoryItems(historyItems)

      if (!silent) {
        setError(feedWarning)
      } else if (!feedWarning) {
        // Silent poll recovered: clear a stale feed error without surfacing new warnings during poll.
        setError(null)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load dashboard data"
      if (!silent) {
        setError(message)
      }
    } finally {
      if (!silent) {
        setLoading(false)
      }
    }
  }, [])

  const recentTrades = useMemo(
    () => buildRecentTradesAndPositions(portfolio, tradeHistoryItems, mt5DealsLedger),
    [portfolio, tradeHistoryItems, mt5DealsLedger],
  )

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    const interval = setInterval(() => {
      void refresh({ silent: true })
    }, DASHBOARD_AUTO_REFRESH_MS)
    return () => clearInterval(interval)
  }, [refresh])

  // Near-real-time updates for portfolio value cards without running heavy analysis endpoints.
  useEffect(() => {
    let cancelled = false
    const tick = async () => {
      try {
        const p = await fetchLiveMetrics()
          .then((x) => x.portfolio)
          .catch(() => fetchAgentPortfolio())
        if (cancelled) return
        setPortfolio(p)
        setPortfolioLiveUpdatedAt(new Date().toISOString())
        setPortfolioLiveError(null)
      } catch (err) {
        if (cancelled) return
        setPortfolioLiveError(err instanceof Error ? err.message : "Portfolio live refresh failed")
      }
    }
    void tick()
    const interval = setInterval(() => {
      void tick()
    }, PORTFOLIO_LIVE_REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  const marketRows = useMemo<MarketRow[]>(() => {
    if (quotes.length === 0) {
      return []
    }
    return quotes.map((q) => {
      const symbol = String(q.requested_symbol || q.symbol || "")
      const pair = toPair(symbol)
      const rawPct = q.change_pct
      const pct = typeof rawPct === "number" && Number.isFinite(rawPct) ? rawPct : null
      return {
        pair,
        bid: formatPriceForSymbol(symbol, Number(q.bid ?? 0)),
        ask: formatPriceForSymbol(symbol, Number(q.ask ?? 0)),
        change: pct === null ? "—" : formatPct(pct),
        trend: pct === null ? ("flat" as const) : pct >= 0 ? ("up" as const) : ("down" as const),
      }
    })
  }, [quotes])

  const agentFeed = useMemo(() => {
    const out: AgentDashboardData["agentFeed"] = []
    const summarize = (response: unknown): string => {
      if (response == null) return "No response payload."
      if (typeof response === "string") return response
      if (typeof response === "number" || typeof response === "boolean") return String(response)
      if (Array.isArray(response)) return `Array response (${response.length} items)`
      const r = response as Record<string, unknown>
      const fetched = r.fetched_headlines
      const headlines = r.headlines
      const nFetched = Array.isArray(fetched) ? fetched.length : 0
      const nHeadlines = Array.isArray(headlines) ? headlines.length : 0
      if (nFetched > 0 || nHeadlines > 0) {
        const articles = r.articles != null ? String(r.articles) : "?"
        const rel = r.relevant_articles != null ? String(r.relevant_articles) : "?"
        const signal = r.market_signal != null ? String(r.market_signal) : "n/a"
        return `Articles=${articles} relevant=${rel} signal=${signal} · ${nFetched || nHeadlines} headline(s) — open trace for full list.`
      }
      if (r.agent === "technical_agent") {
        const sym = String(r.symbol ?? "")
        const dir = String(r.direction ?? "").toLowerCase()
        if (dir === "buy" || dir === "sell") {
          const c = typeof r.confidence === "number" ? r.confidence : Number(r.confidence)
          const pat = String(r.pattern_used ?? "—")
          return `${sym} · ${dir.toUpperCase()} · confidence ${Number.isFinite(c) ? c.toFixed(2) : "?"} · pattern ${pat}`
        }
        const st = String(r.signal_state ?? "diagnostic")
        const hint = String(r.reasoning ?? "").replace(/\s+/g, " ").slice(0, 100)
        return `${sym} · ${st}${hint ? ` — ${hint}${String(r.reasoning ?? "").length > 100 ? "…" : ""}` : ""}`
      }
      const entries = Object.entries(r).slice(0, 2)
      return entries.map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" | ")
    }
    const detailLines = (response: unknown): string[] => {
      if (response == null || typeof response !== "object") return [String(response)]
      const r = response as Record<string, unknown>
      const isTechnicalPayload =
        r.agent === "technical_agent" ||
        (typeof r.direction === "string" &&
          ["buy", "sell"].includes(String(r.direction).toLowerCase()) &&
          r.indicators != null &&
          typeof r.indicators === "object")
      if (isTechnicalPayload) {
        const order = [
          "agent",
          "symbol",
          "signal_state",
          "direction",
          "entry",
          "stop_loss",
          "take_profit",
          "confidence",
          "reasoning",
          "explanation",
          "pattern_used",
        ] as const
        const lines: string[] = []
        for (const k of order) {
          if (r[k] === undefined || r[k] === null) continue
          lines.push(`${k}: ${typeof r[k] === "object" ? JSON.stringify(r[k]) : String(r[k])}`)
        }
        if (r.indicators && typeof r.indicators === "object") {
          lines.push(`indicators: ${JSON.stringify(r.indicators, null, 2)}`)
        }
        return lines
      }
      return Object.entries(r).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    }
    // Prefer building from transparency records so cycle_recorded_at is available.
    // Fall back to pairAnalysis if transparency isn't loaded yet.
    const records = transparency?.records
    if (records && records.length > 0) {
      records.forEach((record) => {
        const sym = record.resolved_symbol ?? record.selected_symbol ?? record.requested_symbol ?? ""
        const cycleRecordedAt = record.cycle_recorded_at ?? null
        const traceSteps = Array.isArray(record.agent_trace) ? record.agent_trace : []
        traceSteps.forEach((step, idx) => {
          out.push({
            id: `${sym}-${step.timestamp}-${idx}`,
            agent: step.agent,
            stage: step.stage,
            symbol: sym,
            timestamp: step.timestamp,
            cycleRecordedAt,
            status: step.status === "ok" ? "completed" : "pending",
            content: summarize(step.response),
            reasoning: detailLines(step.response),
            rawResponse: step.response,
          })
        })
        if (traceSteps.length === 0) {
          const fallbackTs = cycleRecordedAt ?? new Date().toISOString()
          const technicalPayload = record.technical && typeof record.technical === "object" ? record.technical : null
          const marketPayload = record.market && typeof record.market === "object" ? record.market : null
          const precheckPayload = record.precheck && typeof record.precheck === "object" ? record.precheck : null

          if (precheckPayload) {
            out.push({
              id: `${sym}-${fallbackTs}-prefilter`,
              agent: "orchestration_agent",
              stage: "precheck",
              symbol: sym,
              timestamp: fallbackTs,
              cycleRecordedAt,
              status: "completed",
              content: summarize(precheckPayload),
              reasoning: detailLines(precheckPayload),
              rawResponse: precheckPayload,
            })
          }

          if (technicalPayload) {
            out.push({
              id: `${sym}-${fallbackTs}-technical`,
              agent: "technical_agent",
              stage: "analyze",
              symbol: sym,
              timestamp: fallbackTs,
              cycleRecordedAt,
              status: "completed",
              content: summarize(technicalPayload),
              reasoning: detailLines(technicalPayload),
              rawResponse: technicalPayload,
            })
          }

          if (marketPayload) {
            out.push({
              id: `${sym}-${fallbackTs}-market`,
              agent: "market_data_agent",
              stage: "analyze_news",
              symbol: sym,
              timestamp: fallbackTs,
              cycleRecordedAt,
              status: "completed",
              content: summarize(marketPayload),
              reasoning: detailLines(marketPayload),
              rawResponse: marketPayload,
            })
          }
        }
      })
    } else {
      Object.entries(pairAnalysis).forEach(([pair, run]) => {
        ;(run.agent_trace ?? []).forEach((step, idx) => {
          out.push({
            id: `${pair}-${step.timestamp}-${idx}`,
            agent: step.agent,
            stage: step.stage,
            symbol: run.resolved_symbol ?? run.requested_symbol ?? pair.replace("/", ""),
            timestamp: step.timestamp,
            cycleRecordedAt: null,
            status: step.status === "ok" ? "completed" : "pending",
            content: summarize(step.response),
            reasoning: detailLines(step.response),
            rawResponse: step.response,
          })
        })
      })
    }
    out.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1))
    return out
  }, [pairAnalysis, transparency])

  return {
    portfolio,
    portfolioLiveUpdatedAt,
    portfolioLiveError,
    marketRows,
    pairAnalysis,
    agentFeed,
    recentTrades,
    tradeStats,
    transparency,
    agentFleet,
    systemAnalysis,
    portfolioHistory,
    sessionContext,
    maeAnalytics,
    loading,
    error,
    refresh,
  }
}
